import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import withSlideUpModal from '../../utils/hoc/withSlideUpModalComponent'
import { AppDispatch, RootState } from '../../redux-stores/store'
import { setUserTimezone, Timezone } from '../../redux-stores/slices/authSlice'
import { updateUserTimezone } from '../../services/api/auth'
import { getErrorMessage } from '../../utils/helpers'
import { useScrollLock } from '../../hooks/useScrollLock'
import './style.scss'
import { time } from 'framer-motion'

const getDeviceTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
        return ''
    }
}


const pickDefaultTimezoneId = (timezoneList: Timezone[]) => {
    if (timezoneList.length === 0) return null

    const deviceTimezone = getDeviceTimezone()
    const match = timezoneList.find(
        (timezone) => timezone.iana_code.toLowerCase() === deviceTimezone.toLowerCase()
    )

    return match?.id ?? timezoneList[0].id
}

const SelectTimezoneModal: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const timezoneList = useSelector((state: RootState) => state.auth.master?.timezone_list ?? [])
    const { lockScroll, unlockScroll } = useScrollLock()
    const [selectedId, setSelectedId] = useState<number | null>(() => pickDefaultTimezoneId(timezoneList))
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        lockScroll()
        return () => unlockScroll()
    }, [lockScroll, unlockScroll])

    useEffect(() => {
        setSelectedId((current) => {
            if (current && timezoneList.some((timezone) => timezone.id === current)) {
                return current
            }
            return pickDefaultTimezoneId(timezoneList)
        })
    }, [timezoneList])

    const selectedTimezone = useMemo(
        () => timezoneList.find((timezone) => timezone.id === selectedId) ?? null,
        [selectedId, timezoneList]
    )

    const handleSubmit = async () => {
        if (!selectedTimezone || submitting) return

        try {
            setSubmitting(true)
            const response = await updateUserTimezone(selectedTimezone.id)
            const timezoneFromResponse = response?.timezone as Partial<Timezone> | undefined
            dispatch(setUserTimezone(
                timezoneFromResponse?.id
                    ? {
                        id: timezoneFromResponse.id,
                        iana_code: timezoneFromResponse.iana_code ?? selectedTimezone.iana_code,
                        name: timezoneFromResponse.name ?? selectedTimezone.name,
                        utc_offset: timezoneFromResponse.utc_offset ?? selectedTimezone.utc_offset,
                    }
                    : selectedTimezone
            ))
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to save timezone. Please try again.'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="select-timezone-modal scrollable-modal-body p-0">
            <div className="select-timezone-modal__content">
                <div className='new-slide-up-modal-heading d-flex align-items-center gap-2 mt-3 mb-0'>
                    <div className="select-timezone-modal__icon" aria-hidden="true">
                        <i className="fa fa-globe"></i>
                    </div>
                    Set your timezone

                    {/* <h4 className="new-slide-up-modal-heading mt-3 mb-0"></h4> */}
                </div>

                <div className="select-timezone-modal__list" role="listbox" aria-label="Timezone">
                    {timezoneList.map((timezone) => {
                        const isSelected = timezone.id === selectedId
                        const offset = timezone.utc_offset

                        return (
                            <button
                                key={timezone.id}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                className={`select-timezone-modal__option ${isSelected ? 'is-selected' : ''}`}
                                onClick={() => setSelectedId(timezone.id)}
                            >
                                <span className="select-timezone-modal__option-copy">
                                    <span className="select-timezone-modal__option-name">{timezone.name}</span>
                                    <span className="select-timezone-modal__option-offset">{timezone.iana_code} {offset}</span>
                                </span>
                                <span className={`select-timezone-modal__radio ${isSelected ? 'is-selected' : ''}`} />
                            </button>
                        )
                    })}
                </div>
            </div>

            <div className="campaign-apply-ig-connect-benefit-actions scrollable-modal-footer pb-5">
                <div className="px-3">
                    <button
                        type="button"
                        className="btn btn-primary w-100"
                        disabled={!selectedTimezone || submitting}
                        onClick={handleSubmit}
                    >
                        {submitting ? 'Saving...' : 'Continue'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default withSlideUpModal(SelectTimezoneModal, undefined, true)
